import axios from 'axios';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in requests
});

/**
 * Which domain controller the operator is currently working on.
 *
 * Kept in localStorage rather than only in redux so it survives a reload and is
 * available to this interceptor, which runs outside React. The backend reads it
 * from the X-Samba-Agent header on EVERY request, so one selection steers every
 * view, and an absent header falls back to the default agent server-side.
 */
const AGENT_KEY = 'samba_agent';

export const getSelectedAgent = () => {
  try {
    return localStorage.getItem(AGENT_KEY) || null;
  } catch {
    return null; // private mode / blocked storage
  }
};

export const setSelectedAgent = (name) => {
  try {
    if (name) localStorage.setItem(AGENT_KEY, name);
    else localStorage.removeItem(AGENT_KEY);
  } catch {
    /* non-fatal: the session still works, it just will not persist */
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // A caller may target a SPECIFIC agent regardless of the global
    // selection -- the per-agent service controls on the Agents page do
    // exactly that. Never clobber a header the caller set deliberately, or
    // "restart dc-b" would restart whichever DC happened to be selected.
    if (!config.headers['X-Samba-Agent']) {
      const agent = getSelectedAgent();
      if (agent) {
        config.headers['X-Samba-Agent'] = agent;
      }
    }
    // A file upload must NOT keep the client's default JSON content type.
    // FormData needs a multipart boundary, which only the browser can
    // generate, and it only generates one if we leave the header alone.
    // Sending FormData labelled application/json produces a body the server
    // cannot parse at all -- request.FILES arrives empty and the endpoint
    // reports "no file was uploaded", which points the reader at the file
    // rather than at this header.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect to login for sudo/SSH validation failures
      // These are credential validation endpoints, not session auth
      const url = error.config?.url || '';
      const isCredentialValidation = url.includes('/auth/sudo') || url.includes('/auth/ssh/test');

      if (!isCredentialValidation) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  // Samba/LDAP auth (legacy)
  login: (username, password) =>
    api.post('/auth/login/', { username, password }),
  logout: () => api.post('/auth/logout/'),
  validateSudoPassword: (username, password) =>
    api.post('/auth/sudo/', { username, password }),
  checkAuthStatus: () => api.get('/auth/status/'),
  refreshSudoSession: () => api.post('/auth/sudo/refresh/'),
};

// App Authentication API (Phase 1-5)
export const appAuthAPI = {
  // Get available auth methods
  getAuthMethods: () => api.get('/app-auth/auth-methods/'),

  // Password authentication
  login: (username, password, totpToken = null) => {
    const data = { username, password };
    if (totpToken) {
      data.totp_token = totpToken;
    }
    return api.post('/app-auth/login/', data);
  },
  logout: () => api.post('/app-auth/logout/'),
  getCurrentUser: () => api.get('/app-auth/me/'),
  changePassword: (oldPassword, newPassword) =>
    api.post('/app-auth/change-password/', { old_password: oldPassword, new_password: newPassword }),

  // TOTP/2FA (for current user)
  listTOTPDevices: () => api.get('/app-auth/totp/devices/'),
  setupTOTP: (deviceName) =>
    api.post('/app-auth/totp/setup/', { device_name: deviceName }),
  confirmTOTP: (deviceId, token) =>
    api.post('/app-auth/totp/confirm/', { device_id: deviceId, token }),
  disableTOTP: (password) =>
    api.post('/app-auth/totp/disable/', { password }),
  sendTOTPEmail: () => api.post('/app-auth/totp/send-email/'),

  // Admin TOTP Management (for managing other users)
  listUserTOTPDevices: (username) => api.get(`/app-auth/users/${username}/totp/devices/`),
  disableUserTOTP: (username) => api.post(`/app-auth/users/${username}/totp/disable/`),

  // Admin WebAuthn Management (for managing other users)
  listUserWebAuthnCredentials: (username) => api.get(`/app-auth/users/${username}/webauthn/credentials/`),
  deleteUserWebAuthnCredential: (username, credentialId) =>
    api.delete(`/app-auth/users/${username}/webauthn/credentials/${credentialId}/delete/`),

  // Admin OIDC Management (for managing other users)
  listUserOIDCLinks: (username) => api.get(`/app-auth/users/${username}/oidc/links/`),
  unlinkUserOIDCAccount: (username, providerId) =>
    api.delete(`/app-auth/users/${username}/oidc/links/${providerId}/unlink/`),

  // WebAuthn/Passkeys
  listWebAuthnCredentials: () => api.get('/app-auth/webauthn/credentials/'),
  startWebAuthnRegistration: (credentialName, authenticatorAttachment) =>
    api.post('/app-auth/webauthn/register/start/', {
      credential_name: credentialName,
      authenticator_attachment: authenticatorAttachment
    }),
  completeWebAuthnRegistration: (credentialName, registrationResponse, expectedChallenge) =>
    api.post('/app-auth/webauthn/register/complete/', {
      credential_name: credentialName,
      registration_response: registrationResponse,
      expected_challenge: expectedChallenge
    }),
  startWebAuthnAuthentication: (username) =>
    api.post('/app-auth/webauthn/authenticate/start/', { username }),
  completeWebAuthnAuthentication: (credentialId, authenticationResponse, expectedChallenge) =>
    api.post('/app-auth/webauthn/authenticate/complete/', {
      credential_id: credentialId,
      authentication_response: authenticationResponse,
      expected_challenge: expectedChallenge
    }),
  deleteWebAuthnCredential: (credentialId) =>
    api.delete(`/app-auth/webauthn/credentials/${credentialId}/delete/`),

  // OIDC
  listOIDCProviders: () => api.get('/app-auth/oidc/providers/'),
  startOIDCAuth: (providerId, redirectUri) =>
    api.post('/app-auth/oidc/auth/start/', {
      provider_id: providerId,
      redirect_uri: redirectUri
    }),
  completeOIDCAuth: (providerId, code, state, redirectUri) =>
    api.post('/app-auth/oidc/auth/callback/', {
      provider_id: providerId,
      code,
      state,
      redirect_uri: redirectUri
    }),
  listOIDCLinks: () => api.get('/app-auth/oidc/links/'),
  unlinkOIDCAccount: (providerId) =>
    api.delete(`/app-auth/oidc/links/${providerId}/unlink/`),

  // Password reset
  requestPasswordReset: (email) =>
    api.post('/app-auth/password-reset/request/', { email }),
  confirmPasswordReset: (token, newPassword) =>
    api.post('/app-auth/password-reset/confirm/', {
      token,
      new_password: newPassword
    }),

  // User management (admin)
  listUsers: () => api.get('/app-auth/users/'),
  createUser: (userData) => api.post('/app-auth/users/create/', userData),
  getUser: (username) => api.get(`/app-auth/users/${username}/`),
  updateUser: (username, userData) =>
    api.put(`/app-auth/users/${username}/update/`, userData),
  deleteUser: (username) =>
    api.delete(`/app-auth/users/${username}/delete/`),
  adminResetPassword: (username, newPassword, sendEmail) =>
    api.post(`/app-auth/users/${username}/reset-password/`, {
      new_password: newPassword,
      send_email: sendEmail
    }),

  // Audit logs
  getAuditLogs: (params) => api.get('/app-auth/audit-logs/', { params }),

  // Email Configuration
  getEmailConfig: () => api.get('/app-auth/email-config/'),
  updateEmailConfig: (configData) => api.post('/app-auth/email-config/update/', configData),
  testEmailConfig: (recipientEmail) => api.post('/app-auth/email-config/test/', { recipient_email: recipientEmail }),

  // Password Reset with Email TOTP
  requestPasswordResetEmail: (email) => api.post('/app-auth/password-reset/email/request/', { email }),
  verifyPasswordResetCode: (email, code, newPassword) =>
    api.post('/app-auth/password-reset/email/verify/', {
      email,
      code,
      new_password: newPassword
    }),

  // Password Policy (Admin)
  getPasswordPolicy: () => api.get('/app-auth/password-policy/'),
  updatePasswordPolicy: (policyData) => api.post('/app-auth/password-policy/update/', policyData),
  testPasswordPolicy: (password, username = '') =>
    api.post('/app-auth/password-policy/test/', { password, username }),
  getPasswordRequirements: () => api.get('/app-auth/password-requirements/'),
};

// SSH Configuration API
export const sshAPI = {
  getStatus: () => api.get('/auth/ssh/status/'),
  getConfig: () => api.get('/auth/ssh/config/'),
  setupWizard: (configData) => api.post('/auth/ssh/setup/', configData),
  updateConfig: (configData) => api.put('/auth/ssh/config/update/', configData),
  deleteConfig: () => api.delete('/auth/ssh/config/delete/'),
  testConnection: (configData = null) =>
    api.post('/auth/ssh/test/', configData || {}),
};

// Users API
export const usersAPI = {
  list: (params = {}) => api.get('/users/', { params }),
  // `refresh` forces the backend to read the DIRECTORY instead of serving its
  // sync cache. The edit dialog must use it: the cache is populated from
  // list_users, which does not return givenName/sn, so a cached record
  // pre-fills the form with blanks for attributes that are in fact set --
  // and the form submits what it shows, which would clear them.
  get: (username, { refresh = false } = {}) =>
    api.get(`/users/${username}/`, refresh ? { params: { refresh: 'true' } } : {}),
  getUserGroups: (username) => api.get(`/users/${username}/groups/`),
  create: (userData) => api.post('/users/create/', userData),
  update: (username, userData) => api.put(`/users/${username}/update/`, userData),
  delete: (username) => api.delete(`/users/${username}/delete/`),
  // The field MUST be `new_password`. ResetPasswordSerializer declares
  // new_password (required, min_length 8), so sending `password` failed validation with
  // 400 {"new_password": ["This field is required."]} on every attempt — the AD password
  // was never changed and the UI reported nothing useful. Measured on the vestahhc
  // instance 2026-10-09: the target user's pwdLastSet was 276 days old.
  resetPassword: (username, newPassword) =>
    api.post(`/users/${username}/password/reset/`, { new_password: newPassword }),
  disable: (username) => api.post(`/users/${username}/disable/`),
  enable: (username) => api.post(`/users/${username}/enable/`),
  getAuditLogs: (params) => api.get('/users/audit/logs/', { params }),
  listManagers: () => api.get('/users/managers/'),
};

// Groups API
export const groupsAPI = {
  list: (params = {}) => api.get('/groups/', { params }),
  create: (groupData) => api.post('/groups/create/', groupData),
  delete: (groupName) => api.delete(`/groups/${groupName}/`),
  getMembers: (groupName) => api.get(`/groups/${groupName}/members/`),
  addMembers: (groupName, usernames) =>
    api.post(`/groups/${groupName}/members/add/`, { usernames }),
  removeMembers: (groupName, usernames) =>
    api.delete(`/groups/${groupName}/members/remove/`, { data: { usernames } }),
  nestGroup: (groupName, parentGroups) =>
    api.post(`/groups/${groupName}/nest/`, { parent_groups: parentGroups }),
};

// OUs API
export const ousAPI = {
  list: () => api.get('/ous/'),
  create: (ouData) => api.post('/ous/create/', ouData),
  delete: (dn) => api.delete(`/ous/delete/`, { data: { dn } }),
  rename: (dn, newName) => api.put(`/ous/rename/`, { dn, new_name: newName }),
};

// LDAP API
export const ldapAPI = {
  search: (searchData) => api.post('/ldap/search/', searchData),
  browse: (baseDn) => api.get('/ldap/browse/', { params: { base_dn: baseDn } }),
  modify: (modifyData) => api.post('/ldap/modify/', modifyData),
  export: (exportData) => api.post('/ldap/export/', exportData),
  getHistory: (params) => api.get('/ldap/history/', { params }),
};

// Schema API
export const schemaAPI = {
  getAttributes: (params = {}) => api.get('/schema/attributes/', { params }),
  getObjectClasses: (params = {}) => api.get('/schema/objectclasses/', { params }),
  addAttribute: (attributeData) => api.post('/schema/attributes/add/', attributeData),
  modifyObjectClass: (classData) =>
    api.post('/schema/objectclasses/modify/', classData),
  getLogs: (params) => api.get('/schema/logs/', { params }),
};

// Diagnostics API
export const diagnosticsAPI = {
  getStatus: () => api.get('/diagnostics/status/'),
  runDiagnostics: (type) => api.post('/diagnostics/run/', { type }),
  getHistory: (params) => api.get('/diagnostics/history/', { params }),
  getLogs: (params) => api.get('/diagnostics/logs/', { params }),

  // Service management
  startService: () => api.post('/diagnostics/service/start/'),
  stopService: () => api.post('/diagnostics/service/stop/'),
  restartService: () => api.post('/diagnostics/service/restart/'),
  getServiceStatusDetailed: () => api.get('/diagnostics/service/status/'),
};

// GPO API
export const gpoAPI = {
  list: () => api.get('/gpo/'),
  get: (gpoName) => api.get(`/gpo/${gpoName}/`),
  create: (gpoData) => api.post('/gpo/create/', gpoData),
  delete: (gpoName) => api.delete(`/gpo/${gpoName}/delete/`),
  link: (linkData) => api.post('/gpo/link/create/', linkData),
  unlink: (unlinkData) => api.post('/gpo/link/remove/', unlinkData),
  getLinks: (containerDn) => api.get('/gpo/link/list/', { params: { container_dn: containerDn } }),
  getAuditLogs: (params) => api.get('/gpo/audit/logs/', { params }),
};

export default api;

// Agent control plane API
//
// Two mounts on the backend with two different audiences. Everything here is
// the OPERATOR half (`/api/agents/...`, normal JWT). The agent half
// (`/api/agent/register|checkin|result`) is bearer-token and is never called
// from the browser -- if you find yourself adding it here, something is wrong.
export const agentsAPI = {
  list: () => api.get('/agents/'),
  // Returns the enrollment token in plaintext ONCE. It is stored hashed and
  // cannot be shown again; the UI must surface it immediately.
  create: (data) => api.post('/agents/create/', data),
  reissueEnrollment: (name, data = {}) => api.post(`/agents/${name}/enroll/`, data),
  // Rename / re-describe an agent. Safe for mTLS: a client certificate is
  // matched to its agent by serial, not by its CN, so the channel survives.
  rename: (name, data) => api.patch(`/agents/${name}/rename/`, data),
  // What THIS application records about a trust: who established it, when, the
  // addresses the two sides actually reach each other on, last validation.
  trustDetail: (agentName, domain) =>
    api.get(`/agents/trusts/${encodeURIComponent(domain)}/`,
            { headers: { 'X-Samba-Agent': agentName } }),
  updateTrustDetail: (agentName, domain, data) =>
    api.patch(`/agents/trusts/${encodeURIComponent(domain)}/`, data,
              { headers: { 'X-Samba-Agent': agentName } }),
  revoke: (name) => api.post(`/agents/${name}/revoke/`),
  jobs: (params) => api.get('/agents/jobs/', { params }),
  getPolicy: () => api.get('/agents/policy/'),
  setPolicy: (data) => api.post('/agents/policy/set/', data),

  // Service control, scoped to ONE named domain controller.
  //
  // These live here rather than on the Diagnostics page because "restart the
  // service" is meaningless without saying which controller: the request is
  // routed by the X-Samba-Agent header, and the header is set explicitly from
  // the card rather than inherited from whatever is globally selected.
  startService: (name) =>
    api.post('/diagnostics/service/start/', {}, { headers: { 'X-Samba-Agent': name } }),
  stopService: (name) =>
    api.post('/diagnostics/service/stop/', {}, { headers: { 'X-Samba-Agent': name } }),
  restartService: (name) =>
    api.post('/diagnostics/service/restart/', {}, { headers: { 'X-Samba-Agent': name } }),
  serviceStatus: (name) =>
    api.get('/diagnostics/service/status/', { headers: { 'X-Samba-Agent': name } }),

  // Agent releases. The hub only ANNOUNCES: it holds no signing key, and every
  // agent verifies an ed25519 signature against a key compiled into its own
  // binary before a single byte is installed.
  // Cross-domain trusts, always scoped to one named DC: a trust belongs to a
  // domain, and with several enrolled you must say whose.
  listTrusts: (name) =>
    api.post('/agents/trusts/', { action: 'list' }, { headers: { 'X-Samba-Agent': name } }),
  validateTrust: (name, domain) =>
    api.post('/agents/trusts/', { action: 'validate', domain },
             { headers: { 'X-Samba-Agent': name } }),
  showTrust: (name, domain) =>
    api.post('/agents/trusts/', { action: 'show', domain },
             { headers: { 'X-Samba-Agent': name } }),
  createTrust: (name, args) =>
    api.post('/agents/trusts/', { action: 'create', ...args },
             { headers: { 'X-Samba-Agent': name } }),
  deleteTrust: (name, args) =>
    api.post('/agents/trusts/', { action: 'delete', ...args },
             { headers: { 'X-Samba-Agent': name } }),

  getRelease: () => api.get('/agents/release/'),
  stageRelease: (manifest, signature) =>
    api.post('/agents/release/', { manifest, signature }),
  setRollout: (rollout) => api.post('/agents/release/', { rollout }),
};
